import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Sparkle } from "@phosphor-icons/react";
import { MaskLine } from "@/components/site/Reveal";
import { StoreButtons } from "@/components/site/StoreButtons";
import { Phone, AnalysisScreen } from "@/components/site/PhoneMockup";
import { usePageNav } from "@/components/site/nav";
import { staggerParent } from "@/lib/motion";

export const Hero = () => {
  const { goToSection } = usePageNav();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const phoneRotate = useTransform(scrollYProgress, [0, 1], [0, -4]);

  return (
    <section
      ref={ref}
      className="relative z-10 mx-auto flex min-h-screen max-w-6xl flex-col justify-center px-4 pb-16 pt-32 sm:px-6 lg:pt-36"
      data-testid="hero-section"
    >
      <div className="grid items-center gap-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-teal-500/25 bg-teal-500/10 px-4 py-1.5"
          >
            <Sparkle size={14} weight="fill" className="text-teal-400" />
            <span className="text-xs font-medium tracking-wide text-teal-300">
              AI-Powered Crypto Intelligence
            </span>
          </motion.div>

          <motion.h1
            variants={staggerParent}
            initial="hidden"
            animate="show"
            className="font-display text-5xl font-extrabold leading-[1.02] tracking-tighter text-white sm:text-6xl lg:text-7xl"
          >
            <MaskLine custom={0}>Smarter Crypto</MaskLine>
            <MaskLine custom={1}>Decisions.</MaskLine>
            <MaskLine custom={2}>
              <span className="text-gradient">Powered by Intelligence.</span>
            </MaskLine>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-white/60 sm:text-lg"
          >
            Velora Intelligence combines AI-powered analysis, market
            intelligence, risk assessment and smart monitoring in one platform —
            helping you understand crypto markets and make more informed
            decisions.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
          >
            <StoreButtons className="mt-8" />
            <button
              onClick={() => goToSection("features")}
              data-testid="hero-explore-features"
              className="group mt-6 inline-flex items-center gap-2 text-sm font-medium text-white/70 transition-colors hover:text-teal-400"
            >
              Explore Features
              <ArrowDown
                size={16}
                className="transition-transform duration-300 group-hover:translate-y-1"
              />
            </button>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            style={{ y: phoneY, rotate: phoneRotate }}
            initial={{ opacity: 0, scale: 0.92, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="animate-float-slow"
          >
            <Phone testid="hero-phone-mockup">
              <AnalysisScreen />
            </Phone>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
